import { useEffect, useMemo, useState } from "react";
import { Mask } from "@/types/mask";
import { PcmVersion } from "@/types/pcmVersion";
import { Project } from "@/types/project";
import { WaferDie } from "@/components/chipPos/ChipPosSelector";

export type CreateMaskData = {
  name: string;
  pcmVersion: string;
  waferDiameterInMm: string;
  oTestVersion: string;
  dieWidth: string;
  dieHeight: string;
  dieOffsetX: string;
  dieOffsetY: string;
  referenceDieX: string;
  referenceDieY: string;
  edgeIndent: string;
};

export type WaferVisualizationData = Pick<
  CreateMaskData,
  | "waferDiameterInMm"
  | "dieWidth"
  | "dieHeight"
  | "dieOffsetX"
  | "dieOffsetY"
  | "referenceDieX"
  | "referenceDieY"
  | "edgeIndent"
>;

export const defaultFormValues: CreateMaskData = {
  name: "",
  pcmVersion: "",
  waferDiameterInMm: "200",
  oTestVersion: "",
  dieWidth: "22310",
  dieHeight: "14870",
  dieOffsetX: String(22310 / 2),
  dieOffsetY: String(14870 / 2),
  referenceDieX: "",
  referenceDieY: "",
  edgeIndent: "5",
};

export const waferDiameterOptions = [
  { value: "200", label: "200mm", description: "Wafer Diameter" },
  { value: "300", label: "300mm", description: "Wafer Diameter" },
];

export const normalizeValues = (data: CreateMaskData, project: Project) => ({
  name: data.name.trim().toUpperCase(),
  pcm_version: data.pcmVersion.trim(),
  project_id: project.id,
  wafer_diameter_in_mm: Number(data.waferDiameterInMm),
  die_width: data.dieWidth ? Number(data.dieWidth) : undefined,
  die_height: data.dieHeight ? Number(data.dieHeight) : undefined,
  die_offset_x: data.dieOffsetX ? Number(data.dieOffsetX) : undefined,
  die_offset_y: data.dieOffsetY ? Number(data.dieOffsetY) : undefined,
  reference_die_x: data.referenceDieX ? Number(data.referenceDieX) : undefined,
  reference_die_y: data.referenceDieY ? Number(data.referenceDieY) : undefined,
  edge_indent: Number(data.edgeIndent),
});

export const normalizeUpdateValues = (data: CreateMaskData) => ({
  name: data.name.trim().toUpperCase(),
  wafer_diameter_in_mm: Number(data.waferDiameterInMm),
  die_width: data.dieWidth ? Number(data.dieWidth) : null,
  die_height: data.dieHeight ? Number(data.dieHeight) : null,
  die_offset_x: data.dieOffsetX ? Number(data.dieOffsetX) : null,
  die_offset_y: data.dieOffsetY ? Number(data.dieOffsetY) : null,
  reference_die_x: data.referenceDieX ? Number(data.referenceDieX) : null,
  reference_die_y: data.referenceDieY ? Number(data.referenceDieY) : null,
  edge_indent: Number(data.edgeIndent),
});

const maskToFormData = (mask: Mask): CreateMaskData => ({
  name: mask.name,
  pcmVersion: mask.pcmVersion.pcmVersion,
  waferDiameterInMm: mask.waferDiameter?.replace("mm", "") || "200",
  oTestVersion: mask.pcmVersion.oTestVersion || "",
  dieWidth: mask.dieWidth?.toString() || "",
  dieHeight: mask.dieHeight?.toString() || "",
  dieOffsetX:
    mask.dieOffsetX?.toString() ?? (mask.dieWidth != null ? String(mask.dieWidth / 2) : "11155"),
  dieOffsetY:
    mask.dieOffsetY?.toString() ?? (mask.dieHeight != null ? String(mask.dieHeight / 2) : "7435"),
  referenceDieX: mask.referenceDieX?.toString() || "",
  referenceDieY: mask.referenceDieY?.toString() || "",
  edgeIndent: mask.edgeIndent?.toString() ?? "5",
});

const toVisualizationData = (data: CreateMaskData): WaferVisualizationData => ({
  waferDiameterInMm: data.waferDiameterInMm,
  dieWidth: data.dieWidth,
  dieHeight: data.dieHeight,
  dieOffsetX: data.dieOffsetX,
  dieOffsetY: data.dieOffsetY,
  referenceDieX: data.referenceDieX,
  referenceDieY: data.referenceDieY,
  edgeIndent: data.edgeIndent,
});

export function useMaskForm({
  open,
  mask,
  pcmVersions,
}: {
  open: boolean;
  mask?: Mask;
  pcmVersions: PcmVersion[];
}) {
  const initialData = mask ? maskToFormData(mask) : defaultFormValues;

  const [data, setData] = useState<CreateMaskData>(() => initialData);
  const [newlyCreatedPcmVersion, setNewlyCreatedPcmVersion] = useState("");
  const [selectedReferenceDie, setSelectedReferenceDie] = useState<string | null>(() =>
    mask?.referenceDieX !== undefined ? "0:0" : null,
  );
  const [dieOffsetXTouched, setDieOffsetXTouched] = useState(() => mask?.dieOffsetX != null);
  const [dieOffsetYTouched, setDieOffsetYTouched] = useState(() => mask?.dieOffsetY != null);
  const [waferVisualizationData, setWaferVisualizationData] = useState<WaferVisualizationData>(() =>
    toVisualizationData(initialData),
  );

  useEffect(() => {
    if (open) {
      const formData = mask ? maskToFormData(mask) : defaultFormValues;
      setData(formData);
      setWaferVisualizationData(toVisualizationData(formData));
      setSelectedReferenceDie(mask?.referenceDieX !== undefined ? "0:0" : null);
      setDieOffsetXTouched(mask?.dieOffsetX != null);
      setDieOffsetYTouched(mask?.dieOffsetY != null);
    }
  }, [open, mask]);

  const pcmVersionsWithNewlyCreatedPcmVersion = useMemo(() => {
    if (newlyCreatedPcmVersion) {
      return [
        ...pcmVersions,
        { id: "NEW", pcmVersion: newlyCreatedPcmVersion, oTestVersion: newlyCreatedPcmVersion },
      ];
    }
    return pcmVersions;
  }, [newlyCreatedPcmVersion, pcmVersions]);

  const handleChange = (field: keyof CreateMaskData, value: string) => {
    const newValue = value || "";

    if (field === "dieOffsetX") {
      setDieOffsetXTouched(newValue !== "");
    } else if (field === "dieOffsetY") {
      setDieOffsetYTouched(newValue !== "");
    }

    if (
      ["dieWidth", "dieHeight", "dieOffsetX", "dieOffsetY", "waferDiameterInMm"].includes(field)
    ) {
      const resetReferenceDie = field === "waferDiameterInMm";
      if (resetReferenceDie) {
        setSelectedReferenceDie(null);
      }
      setData((prev) => {
        const next: CreateMaskData = {
          ...prev,
          [field]: newValue,
          ...(resetReferenceDie ? { referenceDieX: "", referenceDieY: "" } : {}),
        };
        if (field === "dieWidth" && !dieOffsetXTouched && newValue) {
          next.dieOffsetX = String(Number(newValue) / 2);
        }
        if (field === "dieHeight" && !dieOffsetYTouched && newValue) {
          next.dieOffsetY = String(Number(newValue) / 2);
        }
        return next;
      });
      setWaferVisualizationData((prev) => ({
        ...prev,
        ...(resetReferenceDie ? { referenceDieX: "", referenceDieY: "" } : {}),
        // waferDiameterInMm is changed via a radio button (no blur), update visualization immediately
        ...(field === "waferDiameterInMm" ? { waferDiameterInMm: newValue } : {}),
      }));
    } else {
      setData((prev) => ({ ...prev, [field]: newValue }));
    }
  };

  const handleBlur = (field: keyof CreateMaskData) => {
    if (
      ["dieOffsetX", "dieOffsetY", "waferDiameterInMm", "referenceDieX", "referenceDieY"].includes(
        field,
      )
    ) {
      // If an offset field is cleared, restore the half-dimension default
      if ((field === "dieOffsetX" || field === "dieOffsetY") && !data[field]) {
        const halfValue =
          field === "dieOffsetX"
            ? String(Number(data.dieWidth) / 2)
            : String(Number(data.dieHeight) / 2);
        setData((prev) => ({ ...prev, [field]: halfValue }));
        setWaferVisualizationData((prev) => ({ ...prev, [field]: halfValue }));
        if (field === "dieOffsetX") setDieOffsetXTouched(false);
        if (field === "dieOffsetY") setDieOffsetYTouched(false);
      } else {
        setWaferVisualizationData((prev) => ({ ...prev, [field]: data[field] }));
      }
    } else if (field === "edgeIndent") {
      const value = Number(data[field]);
      const correctedValue = isNaN(value) || value < 0 ? "0" : data[field];
      setData((prev) => ({ ...prev, edgeIndent: correctedValue }));
      setWaferVisualizationData((prev) => ({ ...prev, edgeIndent: correctedValue }));
    } else if (["dieWidth", "dieHeight"].includes(field)) {
      // Ensure die dimensions are at least 5000µm
      const value = Number(data[field]);
      if (isNaN(value) || value < 5000) {
        const correctedValue = "5000";
        const halfValue = String(Number(correctedValue) / 2);
        setData((prev) => {
          const next: CreateMaskData = { ...prev, [field]: correctedValue };
          if (field === "dieWidth" && !dieOffsetXTouched) next.dieOffsetX = halfValue;
          if (field === "dieHeight" && !dieOffsetYTouched) next.dieOffsetY = halfValue;
          return next;
        });
        setWaferVisualizationData((prev) => {
          const next = { ...prev, [field]: correctedValue };
          if (field === "dieWidth" && !dieOffsetXTouched) next.dieOffsetX = halfValue;
          if (field === "dieHeight" && !dieOffsetYTouched) next.dieOffsetY = halfValue;
          return next;
        });
      } else {
        setWaferVisualizationData((prev) => {
          const next = { ...prev, [field]: data[field] };
          if (field === "dieWidth") next.dieOffsetX = data.dieOffsetX;
          if (field === "dieHeight") next.dieOffsetY = data.dieOffsetY;
          return next;
        });
      }
    }
  };

  const handleDieClick = (die: WaferDie) => {
    const waferRadiusUm = ((Number(waferVisualizationData.waferDiameterInMm) || 200) * 1000) / 2;
    const refXStr = (die.centerX - waferRadiusUm).toString();
    const refYStr = (die.centerY - waferRadiusUm).toString();
    setSelectedReferenceDie("0:0");
    setData((prev) => ({ ...prev, referenceDieX: refXStr, referenceDieY: refYStr }));
    setWaferVisualizationData((prev) => ({
      ...prev,
      referenceDieX: refXStr,
      referenceDieY: refYStr,
    }));
  };

  return {
    data,
    setData,
    waferVisualizationData,
    selectedReferenceDie,
    newlyCreatedPcmVersion,
    setNewlyCreatedPcmVersion,
    pcmVersionsWithNewlyCreatedPcmVersion,
    handleChange,
    handleBlur,
    handleDieClick,
  };
}
